import $ from 'jquery';
import React from 'react';
import isEqual from 'react-fast-compare';
import _ from 'underscore';

import JellyfishSidepanel from 'app-core/archetype/sidepanel/JellyfishSidepanel';
import BenchlingConfig from 'BenchlingConfig';
import * as benchlingUtil from 'BenchlingUtil';
import DataConnector from 'connect/DataConnector';
import {NavPreferences, SidepanelExpansionState, Section as NavSection} from 'constants/NavConstants';
import {NavStoreProject} from 'constants/SourceConstants';
import {ErrorHandlingQuery} from 'graphql-client/BenchlingQuery';
import {graphQLArrayToREST} from 'graphql-client/declarative-translations/external/translate';
import {INSIGHTS_PRODUCT} from 'insights/NamingConstants';
import AnalyticsSidepanelListing from 'insights/sidepanel/AnalyticsSidepanelListing';
import {AllSourcesQuery} from 'nav/graphql/__generated/AllSourcesQuery';
import {ALL_SOURCES_QUERY} from 'nav/graphql/AllSources';
import SharedFileSidepanel from 'nav/share/growth/SharedFileSidepanel';
import {showValueMessagingForSharedFile} from 'nav/share/growth/Util';
import {AllAssaySchemasQuery} from 'nav/sidepanel/__generated/AllAssaySchemasQuery';
import SubtemplateStore from 'notebook/entry-templates/SubtemplateStore';
import {Subtemplate, Template, TemplateCollection} from 'notebook/entry-templates/TemplateConstants';
// @ts-expect-error
import TemplateStore from 'notebook/entry-templates/TemplateStore';
import {TFIAccount, TFIFolder, TFIObject} from 'notebook/tfi/TFIConstants';
import TFIStore from 'notebook/tfi/TFIStore';
import preferences from 'Preferences';
import ProcessManagementSidepanel from 'processes/dashboard/ProcessManagementSidepanel';
import RequestsV2Sidepanel from 'processes/requests_v2/dashboard/RequestsV2Sidepanel';
import {renderToDOM} from 'redux-stores/ReduxDOMRender';
import RegistryStore from 'registry/RegistryStore';
import RequestsSidepanel from 'requests/dashboard/RequestsSidepanel';
import {getLegacyRequestObjectName} from 'requests/RequestConstants';
import {Section as SidebarSection} from 'sidebar/SidebarConstants';
import WorkspaceStore from 'sidebar/WorkspaceStore';
import EntityStore from 'stores/EntityStore';
import StudiesSidepanel from 'studies/sidepanel/StudiesSidepanel';
import setTestAttrs from 'test/lib/cypress/setTestAttrs';
import {TRANSITION_SLIDE_SIDEPANEL_DURATION_MS} from 'ui-library/TransitionConstants';
import WorkflowsSidepanel from 'workflows/WorkflowsSidepanel';
import {WorklistSidepanelSelector, WorklistSidepanelState} from 'worklists/reduceWorklistSidepanelActions';
import WorklistItemsSidepanel from 'worklists/sidepanel-components/WorklistItemsSidepanel';
import WorklistsSidepanel from 'worklists/sidepanel-components/WorklistsSidepanel';
import * as WorklistUIActions from 'worklists/WorklistUIActions';

import {isCompactDisplay} from '../NavUtil';
import {AllInventorySchemasQuery} from './__generated/AllInventorySchemasQuery';
import ConnectionsSidepanel from './connections/ConnectionsSidepanel';
import HomeSidepanel from './HomeSidepanel';
import ProjectsSidepanel from './listing/ProjectsSidepanel';
import RegistriesSidepanel from './listing/RegistriesSidepanel';
import SearchSidepanel from './listing/SearchSidepanel';
import StorablesSidepanel from './listing/StorablesSidepanel';
import SettingsSidepanel from './SettingsSidepanel';
import {ALL_ASSAY_SCHEMAS_QUERY, ALL_INVENTORY_SCHEMAS_QUERY} from './SidepanelQueries';
import SidepanelStore from './SidepanelStore';

const ANIMATION_TIMEOUT = 500;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type JQueryNode = any;

function callbackOnTransitionEnd($node: JQueryNode, callback: () => void, timeout: number): void {
  let called = false;
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const run = () => {
    if (called) {
      return;
    }
    called = true;
    $node.off('.sidepaneltransition');
    callback();
  };
  $node.one('transitionend.sidepaneltransition', run);
  setTimeout(run, timeout);
}

const EXPANSION_STATE_TO_CSS_CLASS_MODIFIER: Record<SidepanelExpansionState, string> = {
  [SidepanelExpansionState.CLOSED]: 'closed',
  [SidepanelExpansionState.COLLAPSED]: 'collapsed',
  [SidepanelExpansionState.SEMI_EXPANDED]: 'semi-expanded',
  [SidepanelExpansionState.FULLY_EXPANDED]: 'fully-expanded',
};

function convertExpansionStateToCssClass(expansionState: SidepanelExpansionState): string {
  return `expansion-state--${EXPANSION_STATE_TO_CSS_CLASS_MODIFIER[expansionState]}`;
}

function setSidepanelExpansionStateClassName($node: JQueryNode, newState: SidepanelExpansionState): void {
  Object.values(SidepanelExpansionState).forEach((expansionState) => {
    $node.removeClass(convertExpansionStateToCssClass(expansionState));
  });
  $node.addClass(convertExpansionStateToCssClass(newState));
}

export interface BaseProps {
  renderExpansionChange: (
    expansionState: SidepanelExpansionState,
    callback: () => void,
    timeout: number
  ) => void;
}

interface QueriedProps {
  refetchSources: () => void;
  projects: Array<NavStoreProject>;
  areSourcesLoading: boolean;
}

interface EnhancedProps {
  activeRegistryId: string | null;
  isExpanded: boolean;
  isOpen: boolean;
  expansionState: SidepanelExpansionState;
  section: NavSection | SidebarSection | null;
  tfiSearch: {
    query?: string | null;
    isActive: boolean;
    tfiData: {
      isSearching: boolean;
      results: Array<TFIObject>;
    };
  };
  subtemplate: Subtemplate | null;
  template: Template;
  templateCollection: TemplateCollection;
  tfi: {
    listedFolder: TFIFolder | null;
    accounts: Array<TFIAccount> | null;
    showTFIAddModal: boolean;
  };
  workspace: {
    activeId: string | null;
  };
  worklistSidepanelState: WorklistSidepanelState;
}

class SidepanelConnector extends DataConnector<BaseProps & QueriedProps, EnhancedProps> {
  shouldSubscribeToRedux = true;
  stores = [RegistryStore, SidepanelStore, SubtemplateStore, TemplateStore, TFIStore, WorkspaceStore];

  enhanceProps(): EnhancedProps {
    return {
      activeRegistryId: this.registryStore().getActiveRegistryId(),
      isExpanded: this.sidepanelStore().getIsExpanded(),
      isOpen: this.sidepanelStore().getIsOpen(),
      expansionState: this.sidepanelStore().getExpansionState(),
      section: this.sidepanelStore().getSection(),
      tfiSearch: {
        query: this.tfiStore().getQuery(),
        isActive: this.tfiStore().getSearchActive(),
        tfiData: this.tfiStore().getTFIData(),
      },
      templateCollection: this.templateStore().getCollection(),
      template: this.templateStore().getTemplate(),
      subtemplate: this.subtemplateStore().getSubtemplate(),
      tfi: {
        listedFolder: this.tfiStore().getListedFolder(),
        accounts: this.tfiStore().getAccounts(),
        showTFIAddModal: this.tfiStore().getShowTFIAddModal(),
      },
      workspace: this.workspaceStore().getState(),
      worklistSidepanelState: new WorklistSidepanelSelector(this.reduxState()).getSidepanelState(),
    };
  }
}

interface State {
  isExpanding: boolean;
  isCompactDisplay: boolean;
}

type SidepanelProps = BaseProps & QueriedProps & EnhancedProps;

export class Sidepanel extends React.Component<SidepanelProps, State> {
  static init(): void {
    const $sidepanelNode = $('.sidepanel-mount');

    const renderExpansionChange = (
      expansionState: SidepanelExpansionState,
      callback: () => void,
      timeout: number
    ): void => {
      setSidepanelExpansionStateClassName($sidepanelNode, expansionState);
      setTestAttrs($sidepanelNode, {
        component: 'Sidepanel',
        element: 'root',
        key: expansionState === SidepanelExpansionState.FULLY_EXPANDED ? 'expanded' : 'collapsed',
      });
      callbackOnTransitionEnd($sidepanelNode, callback, timeout);
    };
    renderToDOM(<QueryingSidepanel renderExpansionChange={renderExpansionChange} />, $sidepanelNode[0]);
  }

  constructor(props: SidepanelProps) {
    super(props);
    this.state = {isExpanding: false, isCompactDisplay: isCompactDisplay()};
  }

  componentDidMount(): void {
    this._renderExpansionChangeAndTrack(this.props.expansionState);
  }

  componentDidUpdate(prevProps: SidepanelProps): void {
    if (this.props.expansionState !== prevProps.expansionState) {
      this._renderExpansionChangeAndTrack(this.props.expansionState);
    }
    if (this.props.isExpanded) {
      this._setWindowTitle();
    }
    if (!prevProps.isOpen && this.props.isOpen && EntityStore.currentUser()) {
      // If previously closed, refetch sources (unless you're not signed in, in which case there are none)
      this.props.refetchSources();
    }

    // Signal the buffers to resize after opening/closing the sidepanel
    if (prevProps.isOpen !== this.props.isOpen) {
      // wait for transition to end
      _.delay(() => $(window).trigger('buffersResize'), TRANSITION_SLIDE_SIDEPANEL_DURATION_MS);
    }
  }

  _renderExpansionChangeAndTrack(expansionState: SidepanelExpansionState): void {
    this.setState({isExpanding: true}, () => {
      this.props.renderExpansionChange(
        expansionState,
        () => {
          this.setState({isExpanding: false});
        },
        ANIMATION_TIMEOUT
      );
    });
  }

  _setIsCompactDisplay = (isCompact: boolean): void => {
    this.setState({isCompactDisplay: isCompact});
    preferences.set(NavPreferences.SIDEPANEL_IS_DISPLAY_COMPACT, isCompact);
  };

  _setWindowTitle(): void {
    switch (this.props.section) {
      case NavSection.ANALYTICS:
        benchlingUtil.setWindowTitle(INSIGHTS_PRODUCT);
        break;
      case NavSection.BENCHLING_APPS:
        benchlingUtil.setWindowTitle('Apps');
        break;
      case NavSection.CONNECTIONS:
        benchlingUtil.setWindowTitle('Connections');
        break;
      case NavSection.STORAGE:
        benchlingUtil.setWindowTitle('Inventory');
        break;
      case NavSection.REQUESTS:
        benchlingUtil.setWindowTitle(getLegacyRequestObjectName({capwords: true, plural: true}));
        break;
      case NavSection.REQUESTS_V2_SUBMISSIONS:
      case NavSection.REQUESTS_V2_DEFINITIONS:
        benchlingUtil.setWindowTitle('Requests');
        break;
      case NavSection.PROJECTS:
      case NavSection.PROJECT_ITEMS:
        benchlingUtil.setWindowTitle('Projects');
        break;
      case NavSection.PROCESSES:
        benchlingUtil.setWindowTitle('Workflows');
        break;
      case NavSection.REGISTRIES:
        benchlingUtil.setWindowTitle('Registries');
        break;
      case NavSection.SEARCH:
        benchlingUtil.setWindowTitle('Search');
        break;
      case NavSection.STUDIES:
        benchlingUtil.setWindowTitle('Studies');
        break;
      default:
        // For other sections, by default do not change title or URL.
        // For example, workflows title and URL are managed by WorkflowsSidepanel.
        break;
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  render = () => {
    if (showValueMessagingForSharedFile()) {
      return <SharedFileSidepanel />;
    }
    switch (this.props.section) {
      case NavSection.ANALYTICS:
        return (
          <AnalyticsSidepanelListing
            isCompactDisplay={this.state.isCompactDisplay}
            setIsCompactDisplay={this._setIsCompactDisplay}
            isExpanded={this.props.isExpanded}
            activeItemId={this.props.workspace.activeId}
          />
        );

      case NavSection.HOME:
        return (
          <HomeSidepanel isExpanded={this.props.isExpanded} activeItemId={this.props.workspace.activeId} />
        );
      case NavSection.JELLYFISH:
        return (
          <JellyfishSidepanel
            isCompactDisplay={this.state.isCompactDisplay}
            setIsCompactDisplay={this._setIsCompactDisplay}
            isExpanded={this.props.isExpanded}
            activeItemId={this.props.workspace.activeId}
          />
        );
      case NavSection.PROJECTS:
      case NavSection.PROJECT_ITEMS:
        return (
          <ProjectsSidepanel
            isCompactDisplay={this.state.isCompactDisplay}
            setIsCompactDisplay={this._setIsCompactDisplay}
            isExpanded={this.props.isExpanded}
            projects={this.props.projects}
            activeItemId={this.props.workspace.activeId}
            areProjectsLoading={this.props.areSourcesLoading}
          />
        );
      case NavSection.PROCESSES:
        return (
          <ProcessManagementSidepanel
            isCompactDisplay={this.state.isCompactDisplay}
            setIsCompactDisplay={this._setIsCompactDisplay}
            isExpanded={this.props.isExpanded}
            activeItemId={this.props.workspace.activeId}
          />
        );
      case NavSection.REGISTRIES:
        return (
          <RegistriesSidepanel
            isCompactDisplay={this.state.isCompactDisplay}
            setIsCompactDisplay={this._setIsCompactDisplay}
            isExpanded={this.props.isExpanded}
            activeItemId={this.props.workspace.activeId}
          />
        );

      case NavSection.STORAGE:
        return (
          <StorablesSidepanel
            isCompactDisplay={this.state.isCompactDisplay}
            setIsCompactDisplay={this._setIsCompactDisplay}
            isExpanded={this.props.isExpanded}
            activeItemId={this.props.workspace.activeId}
          />
        );

      case NavSection.REQUESTS:
        return (
          <RequestsSidepanel
            isCompactDisplay={this.state.isCompactDisplay}
            setIsCompactDisplay={this._setIsCompactDisplay}
            isExpanded={this.props.isExpanded}
            activeItemId={this.props.workspace.activeId}
          />
        );

      case NavSection.REQUESTS_V2_SUBMISSIONS:
      case NavSection.REQUESTS_V2_DEFINITIONS:
        return (
          <RequestsV2Sidepanel
            activeSection={this.props.section}
            isCompactDisplay={this.state.isCompactDisplay}
            setIsCompactDisplay={this._setIsCompactDisplay}
            isExpanded={this.props.isExpanded}
            activeItemId={this.props.workspace.activeId}
          />
        );

      case NavSection.TFI:
      case NavSection.BENCHLING_APPS:
      case NavSection.CONNECTIONS:
        return (
          <ConnectionsSidepanel
            activeSection={this.props.section}
            tfi={{...this.props.tfi, search: this.props.tfiSearch}}
            isCompactDisplay={this.state.isCompactDisplay}
            setIsCompactDisplay={this._setIsCompactDisplay}
            isExpanded={this.props.isExpanded}
            activeItemId={this.props.workspace.activeId}
          />
        );

      case BenchlingConfig.STUDIES_ENABLE && NavSection.STUDIES:
        return (
          <StudiesSidepanel
            isCompactDisplay={this.state.isCompactDisplay}
            setIsCompactDisplay={this._setIsCompactDisplay}
            isExpanded={this.props.isExpanded}
            activeItemId={this.props.workspace.activeId}
          />
        );

      case NavSection.SEARCH:
        return (
          <SearchSidepanel
            isCompactDisplay={this.state.isCompactDisplay}
            setIsCompactDisplay={this._setIsCompactDisplay}
            isExpanded={this.props.isExpanded}
            activeItemId={this.props.workspace.activeId}
          />
        );

      case NavSection.WORKFLOWS:
        return <WorkflowsSidepanel isExpanded={this.props.isExpanded} />;

      case BenchlingConfig.WORKLISTS_ENABLE_WORKLISTS && NavSection.WORKLISTS:
        return (
          <WorklistsSidepanel
            isExpanded={this.props.isExpanded}
            isExpanding={this.state.isExpanding}
            isCompactDisplay={this.state.isCompactDisplay}
            setIsCompactDisplay={this._setIsCompactDisplay}
            onOpenWorklist={WorklistUIActions.openWorklistListing}
          />
        );

      case BenchlingConfig.WORKLISTS_ENABLE_WORKLISTS && NavSection.WORKLIST_ITEMS:
        return (
          <WorklistItemsSidepanel
            activeItemId={this.props.workspace.activeId}
            isExpanded={this.props.isExpanded}
            isExpanding={this.state.isExpanding}
            isCompactDisplay={this.state.isCompactDisplay}
            setIsCompactDisplay={this._setIsCompactDisplay}
            clearSelectedWorklist={WorklistUIActions.returnToWorklistsSidepanel}
            selectedWorklistId={this.props.worklistSidepanelState.selectedWorklistId!}
          />
        );

      case SidebarSection.REGISTRY_SETTINGS_BOX_SCHEMAS:
      case SidebarSection.REGISTRY_SETTINGS_CONTAINER_SCHEMAS:
      case SidebarSection.REGISTRY_SETTINGS_GENERAL:
      case SidebarSection.REGISTRY_SETTINGS_LABEL_PRINTING:
      case SidebarSection.REGISTRY_SETTINGS_LOCATION_SCHEMAS:
      case SidebarSection.REGISTRY_SETTINGS_PLATE_SCHEMAS:
      case SidebarSection.REGISTRY_SETTINGS_SCHEMAS:
      case SidebarSection.REGISTRY_SETTINGS_SELECTORS:
      case SidebarSection.SCHEMA_INTERFACE_SETTINGS:
      case SidebarSection.TAG_SCHEMA_SETTINGS:
      case SidebarSection.TENANT_LEVEL_LABEL_PRINTING_SETTINGS:
      case SidebarSection.TENANT_LEVEL_INVENTORY_SETTINGS:
      case SidebarSection.NOTEBOOK_SETTINGS_TEMPLATE:
      case SidebarSection.NOTEBOOK_SETTINGS_TEMPLATE_COLLECTION:
      case SidebarSection.NOTEBOOK_SETTINGS_TEMPLATE_COLLECTIONS:
      case SidebarSection.REQUEST_SCHEMAS:
      case SidebarSection.REQUEST_TASK_SCHEMAS:
      case SidebarSection.ASSAY_RESULT_SCHEMAS:
      case SidebarSection.CONNECTION_SCHEMAS:
      case SidebarSection.INSTRUMENT_GATEWAYS:
      case SidebarSection.PROCESS_SCHEMAS:
      case SidebarSection.DATA_POLICIES:
      case SidebarSection.SCHEMA_POLICIES:
      case SidebarSection.BENCHLING_APPS_SETTINGS:
      case SidebarSection.EVENT_SUBSCRIPTIONS:
      case SidebarSection.BENCHLING_APP_CANVAS_BUILDER:
      case SidebarSection.CONFIG_MIGRATION:
      case SidebarSection.ASSAY_RUN_SCHEMAS:
      case SidebarSection.MONOMERS:
      case SidebarSection.REVIEW_LIFECYCLES:
      case SidebarSection.SAMPLE_TYPES:
      case SidebarSection.STUDY_SCHEMAS:
      case SidebarSection.UNIT_DICTIONARY:
      case SidebarSection.TEST_DEFINITIONS:
      case SidebarSection.ATTACHMENT_PREVIEW:
        return (
          <SettingsSidepanel
            activeRegistryId={this.props.activeRegistryId}
            expansionState={this.props.expansionState}
            section={this.props.section}
            template={this.props.template || this.props.subtemplate}
            templateCollection={this.props.templateCollection}
          />
        );

      default:
        return null;
    }
  };
}

const ConnectedSidepanel = SidepanelConnector.connect(Sidepanel, {
  throttleUpdates: true,
  shouldPropagateNextState: (a, b) => !isEqual(a, b),
});

// Exported for tests
export function adaptGraphQLSourcesToProps(
  data: AllSourcesQuery | undefined
): Omit<QueriedProps, 'refetchSources' | 'areSourcesLoading'> {
  if (_.isEmpty(data)) {
    return {
      projects: [],
    };
  }

  const graphQLSources = data!.sources;
  const restProjects = graphQLArrayToREST(
    'NavStoreProject',
    graphQLSources.flatMap((source) => (source.__typename === 'ProjectNode' ? [source] : []))
  );
  return {projects: restProjects};
}

function QueryingSidepanel(props: BaseProps): JSX.Element {
  // For historical reasons, this component fetches schemas that downstream components rely on. In the future
  // we'll probably want to move the querying closer to the components that actually need the data.
  return (
    <ErrorHandlingQuery<AllInventorySchemasQuery> query={ALL_INVENTORY_SCHEMAS_QUERY}>
      {() => (
        <ErrorHandlingQuery<AllAssaySchemasQuery> query={ALL_ASSAY_SCHEMAS_QUERY}>
          {() => (
            // TODO(Ben): Remove unnecessary query that is currently always skipped
            <ErrorHandlingQuery<AllSourcesQuery> query={ALL_SOURCES_QUERY} skip={true}>
              {({data, refetch, loading}) => (
                <ConnectedSidepanel
                  {...props}
                  {...adaptGraphQLSourcesToProps(data)}
                  refetchSources={refetch}
                  areSourcesLoading={loading}
                />
              )}
            </ErrorHandlingQuery>
          )}
        </ErrorHandlingQuery>
      )}
    </ErrorHandlingQuery>
  );
}

export default QueryingSidepanel;
